"use client";

import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "../utils";

// ---------------------------------------------------------------------------
// Stable references — hoisted so ReactMarkdown can skip re-parsing when the
// `text` prop hasn't changed. Inline objects/arrays create new refs every
// render, defeating React's bailout.
// ---------------------------------------------------------------------------

const REMARK_PLUGINS = [remarkGfm];

const COMPONENTS: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  p: ({ children }) => (
    <p className="my-1.5 first:mt-0 last:mb-0">{children}</p>
  ),
  table: ({ children }) => (
    <div className="not-prose my-3 w-full min-w-0 max-w-full overflow-x-auto">
      <table className="w-full min-w-max border-collapse border border-zinc-300 text-sm">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-zinc-200/80">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => (
    <th className="border border-zinc-300 px-3 py-2 text-left font-semibold text-inherit">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-zinc-300 px-3 py-2 text-inherit">{children}</td>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
    >
      {children}
    </a>
  ),
  pre: ({ children }) => (
    <pre className="not-prose overflow-x-auto rounded-lg bg-zinc-200/60 p-3 text-xs">
      {children}
    </pre>
  ),
  code: ({ className: codeClassName, children, ...props }) => {
    const isBlock = codeClassName?.includes("language-");
    if (isBlock) {
      return (
        <code className={cn("text-xs", codeClassName)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-zinc-200/60 px-1 py-0.5 text-[0.8em] font-medium"
        {...props}
      >
        {children}
      </code>
    );
  },
};

// ---------------------------------------------------------------------------
// MarkdownContent
// ---------------------------------------------------------------------------

interface MarkdownContentProps {
  text: string;
  className?: string;
}

export const MarkdownContent = memo(({
  text,
  className,
}: MarkdownContentProps) => (
    <div
      className={cn(
        "prose prose-sm max-w-none min-w-0 w-full",
        "leading-relaxed",
        "prose-p:my-1.5 prose-pre:my-2 prose-ul:my-1.5 prose-ol:my-1.5",
        "prose-li:my-0.5 prose-headings:my-2 prose-a:text-blue-600",
        "prose-code:before:content-[''] prose-code:after:content-['']",
        "prose-pre:rounded-lg prose-pre:bg-zinc-200/60",
        "prose-hr:my-6",
        "prose-strong:text-inherit",
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={COMPONENTS}>
        {text}
      </ReactMarkdown>
    </div>
  ));
